import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { useSiteConfig } from "../../context/SiteConfigContext";
import contactAPI from "../../api/contactApi";
import { showSuccess, showError } from "../../utils/toastUtils";
import useAlphabetsInput from "../../hooks/useAlphabeticInput";
import useNumericInput from "../../hooks/useNumericInput";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconLocation = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
    <circle cx="12" cy="9" r="2.5" />
  </svg>
);

const IconPhone = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <path
      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.06 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconMail = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M2 7l10 7 10-7" strokeLinecap="round" />
  </svg>
);

const WatermarkSVG = () => (
  <svg className="contact-hero-watermark" viewBox="0 0 400 400" fill="none">
    <circle cx="200" cy="200" r="190" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="140" stroke="white" strokeWidth="0.5" />
    <circle cx="200" cy="200" r="90" stroke="white" strokeWidth="1" />
    <circle cx="200" cy="200" r="40" stroke="white" strokeWidth="0.5" />
    {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((a, i) => {
      const r = (a * Math.PI) / 180;
      return (
        <line
          key={i}
          x1={200 + 40 * Math.cos(r)}
          y1={200 + 40 * Math.sin(r)}
          x2={200 + 190 * Math.cos(r)}
          y2={200 + 190 * Math.sin(r)}
          stroke="white"
          strokeWidth="0.5"
        />
      );
    })}
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
export default function ContactUS() {
  useAlphabetsInput();
  useNumericInput();

  const { config } = useSiteConfig();

  const INITIAL_FORM_DATA = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const heroRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const ruleRef = useRef(null);
  const subRef = useRef(null);
  const infoRef = useRef(null);
  const formRef = useRef(null);

  // ── GSAP Entrance ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof gsap === "undefined") return;
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
    tl.to(eyebrowRef.current, { opacity: 1, duration: 0.6 }, 0.1)
      .to(titleRef.current, { opacity: 1, y: 0, duration: 0.8 }, 0.25)
      .to(ruleRef.current, { opacity: 1, scaleX: 1, duration: 0.6 }, 0.5)
      .to(subRef.current, { opacity: 1, duration: 0.5 }, 0.65)
      .to(infoRef.current, { opacity: 1, x: 0, duration: 0.7 }, 0.75)
      .to(formRef.current, { opacity: 1, x: 0, duration: 0.7 }, 0.9);
  }, []);

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!emailRegex.test(formData.email))
      newErrors.email = "Enter a valid email";
    if (!formData.phone.trim()) newErrors.phone = "Phone is required";
    if (!formData.subject.trim()) newErrors.subject = "Please select a subject";
    if (!formData.message.trim()) newErrors.message = "Message is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      // Merge firstName + lastName into name for the API
      const payload = {
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
        message: `[${formData.subject}] ${formData.message}`,
      };

      const res = await contactAPI.store(payload);
      if (res.code === 200) {
        showSuccess(res.message);
        setFormData(INITIAL_FORM_DATA);
        setErrors({});
      } else {
        showError(res.message);
      }
    } catch (err) {
      showError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Dynamic info from config ───────────────────────────────────────────────
  const address =
    [
      config?.address?.addressLine,
      config?.address?.district,
      config?.address?.state,
    ]
      .filter(Boolean)
      .join(", ") +
    (config?.address?.pincode ? ` — ${config.address.pincode}` : "");

  return (
    <div className="contact-page">
      {/* ── Hero ── */}
      <div className="contact-hero" ref={heroRef}>
        <div className="contact-hero-bg" />
        <WatermarkSVG />
        <p className="contact-hero-eyebrow" ref={eyebrowRef}>
          We'd Love to Hear From You
        </p>
        <h1 className="contact-hero-title" ref={titleRef}>
          Get in <em>Touch</em>
        </h1>
        <div className="contact-hero-rule" ref={ruleRef} />
        <p className="contact-hero-sub" ref={subRef}>
          Reach out for bespoke orders, collaborations, or any query
        </p>
      </div>

      {/* ── Main Grid ── */}
      <div className="contact-main">
        {/* Info Column */}
        <div className="contact-info" ref={infoRef}>
          <div className="contact-info-label">Our Details</div>
          <h2 className="contact-info-heading">
            Crafted with care,
            <br />
            <em>answered with warmth</em>
          </h2>
          <p className="contact-info-desc">
            Every query at BEGUM'S deserves a thoughtful response. Whether you
            seek a bespoke creation, wish to know more about our artisans, or
            need help with your order — we are here.
          </p>

          <div className="contact-detail-card">
            <div className="contact-detail-icon">
              <IconLocation />
            </div>
            <div>
              <div className="contact-detail-label">Our Atelier</div>
              <div className="contact-detail-value">
                {address || "47, Craft Lane, Lajpat Nagar, New Delhi — 110024"}
              </div>
            </div>
          </div>

          <div className="contact-detail-card">
            <div className="contact-detail-icon">
              <IconPhone />
            </div>
            <div>
              <div className="contact-detail-label">Call Us</div>
              <div className="contact-detail-value">
                {config?.phone ? `+91 ${config.phone}` : "+91 98765 43210"}
                {config?.whatsapp && (
                  <>
                    <br />
                    +91 {config.whatsapp} (WhatsApp)
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="contact-detail-card">
            <div className="contact-detail-icon">
              <IconMail />
            </div>
            <div>
              <div className="contact-detail-label">Write to Us</div>
              <div className="contact-detail-value">
                {config?.email || "hello@begums.in"}
              </div>
            </div>
          </div>

          {/* <div className="contact-hours">
            <div className="contact-hours-title">Atelier Hours</div>
            <div className="contact-hours-row">
              <span>Monday – Sunday</span>
              <span>11:00 – 20:30</span>
            </div>
            <div className="contact-hours-row">
              <span>Public Holidays</span>
              <span>Response may be delayed</span>
            </div>
          </div> */}
        </div>

        {/* Form Column */}
        <div className="contact-form-wrap" ref={formRef}>
          <div className="contact-form-eyebrow">Send a Message</div>
          <h2 className="contact-form-title">How can we assist you?</h2>

          <form onSubmit={handleSubmit} noValidate>
            <div className="contact-form-row">
              <div className="contact-field">
                <label className="contact-label">
                  First Name <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  className={`contact-input only-alphabets${errors.firstName ? " is-invalid" : ""}`}
                  name="firstName"
                  placeholder="Priya"
                  value={formData.firstName}
                  onChange={handleChange}
                />
                {errors.firstName && (
                  <span className="contact-error">{errors.firstName}</span>
                )}
              </div>

              <div className="contact-field">
                <label className="contact-label">Last Name</label>
                <input
                  className="contact-input only-alphabets"
                  name="lastName"
                  placeholder="Sharma"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="contact-form-row">
              <div className="contact-field">
                <label className="contact-label">
                  Email <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  className={`contact-input${errors.email ? " is-invalid" : ""}`}
                  type="email"
                  name="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <span className="contact-error">{errors.email}</span>
                )}
              </div>

              <div className="contact-field">
                <label className="contact-label">
                  Phone <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  className={`contact-input only-numeric${errors.phone ? " is-invalid" : ""}`}
                  type="tel"
                  name="phone"
                  maxLength={10}
                  placeholder="+91 98765 43210"
                  value={formData.phone}
                  onChange={handleChange}
                />
                {errors.phone && (
                  <span className="contact-error">{errors.phone}</span>
                )}
              </div>
            </div>

            <div className="contact-field">
              <label className="contact-label">
                Subject <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <div className="contact-select-wrap">
                <select
                  className={`contact-select${errors.subject ? " is-invalid" : ""}`}
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                >
                  <option value="">Select a subject</option>
                  <option>Bespoke Order Enquiry</option>
                  <option>Order Tracking</option>
                  <option>Returns & Exchange</option>
                  <option>Wholesale & Collaboration</option>
                  <option>General Query</option>
                </select>
              </div>
              {errors.subject && (
                <span className="contact-error">{errors.subject}</span>
              )}
            </div>

            <div className="contact-field">
              <label className="contact-label">
                Message <span style={{ color: "#dc3545" }}>*</span>
              </label>
              <textarea
                className={`contact-textarea${errors.message ? " is-invalid" : ""}`}
                name="message"
                placeholder="Tell us how we can help..."
                value={formData.message}
                onChange={handleChange}
              />
              {errors.message && (
                <span className="contact-error">{errors.message}</span>
              )}
            </div>

            <button
              className="contact-submit"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
